import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { portalRoot } from '../../lib/portalRoot';
import { IconX, IconChevronLeft } from '../icons';
import { IconButton } from './Button';

// SlideOver — the "workspace" surface (client-file.md, Jaylen 2026-09-14,
// pick B). A tall panel slides in from the RIGHT and takes most of the
// width; the page underneath stays visible (dimmed) so the operator keeps
// their place in the list. Structure is fixed so every workspace feels the
// same: header row (back/close + whatever the caller puts there), an
// optional strip under it (tabs, banners), a scrolling body, and a footer
// pinned to the bottom so Save is never off-screen.
//
// Under 768px it is a full-screen page with a back arrow in the header
// (no scrim, no X) — same content, same tabs, same footer.
//
// Behaviour mirrors <Modal>: portal to <body>, Escape + scrim click
// dismiss (both go through onBeforeClose so an unsaved-changes guard can
// veto), body scroll lock, Tab focus trap, focus restore on close, and
// popover awareness (calendar / time / combobox panels and Google Places
// are portaled OUTSIDE us — their Escape is theirs).

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
    // Snapshot at open (same reasoning as Modal.jsx — never flip
    // presentation mid-session, it would remount the whole form).
    const [isMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
    });
    return isMobile;
}

// True when Escape is meant for something living on top of the panel
// (a form-control popover or Google Places suggestions), not for us.
export function escapeClaimedElsewhere() {
    if (document.querySelector('[data-bbm-popover]')) return true;
    return [...document.querySelectorAll('.pac-container')]
        .some(el => el.style.display !== 'none' && el.offsetWidth > 0 && el.offsetHeight > 0);
}

/**
 * <SlideOver
 *     label="Client file"          // aria-label
 *     onClose={fn}                 // called AFTER the exit animation
 *     onBeforeClose={fn}           // return false to veto (unsaved changes)
 *     header={<…/>}                // title block + actions (flex row content)
 *     strip={<…/>}                 // optional: tabs / banners under the header
 *     footer={<…/>}                // pinned bottom row
 *     width="max-w-[820px]"        // desktop panel width cap
 * >
 *     …scrolling body…
 * </SlideOver>
 *
 * Exposes `close()` to children through a render-prop-free path: pass
 * `closeRef` and we set `closeRef.current = requestClose` so a Cancel /
 * Back button inside header or footer animates out like the X does.
 */
export function SlideOver({ label, onClose, onBeforeClose, header, strip, footer, children, width = 'max-w-[820px]', closeRef, bodyClassName = '' }) {
    const isMobile = useIsMobile();
    const [open, setOpen] = useState(false);
    const contentRef = useRef(null);
    const previouslyFocusedRef = useRef(null);
    const hasFocusedRef = useRef(false);

    useEffect(() => {
        const id1 = requestAnimationFrame(() => {
            const id2 = requestAnimationFrame(() => setOpen(true));
            return () => cancelAnimationFrame(id2);
        });
        return () => cancelAnimationFrame(id1);
    }, []);

    const requestClose = useCallback(() => {
        if (typeof onBeforeClose === 'function' && onBeforeClose() === false) return;
        setOpen(false);
        setTimeout(onClose, 200);
    }, [onClose, onBeforeClose]);

    const requestCloseRef = useRef(requestClose);
    useEffect(() => {
        requestCloseRef.current = requestClose;
        if (closeRef) closeRef.current = requestClose;
    }, [requestClose, closeRef]);

    // Escape + Tab trap + scroll lock + focus restore — mount-only.
    useEffect(() => {
        previouslyFocusedRef.current = document.activeElement;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (escapeClaimedElsewhere()) return;
                // A dialog stacked on top of us (ConfirmDialog, a Modal
                // opened from inside the panel) owns Escape while it's up.
                const dialogs = document.querySelectorAll('[role="dialog"]');
                if (dialogs.length && dialogs[dialogs.length - 1] !== contentRef.current?.parentElement) return;
                requestCloseRef.current();
                return;
            }
            if (e.key === 'Tab' && contentRef.current) {
                const focusables = contentRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
                if (focusables.length === 0) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }
        };
        const prevOverflow = document.body.style.overflow;
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            const prev = previouslyFocusedRef.current;
            if (prev && typeof prev.focus === 'function' && document.contains(prev)) prev.focus();
        };
    }, []);

    // First focus: the first real field in the body, not the back/close
    // button — once per lifetime (see Modal.jsx for the re-focus bug).
    useEffect(() => {
        if (!open || !contentRef.current || hasFocusedRef.current) return;
        hasFocusedRef.current = true;
        const focusables = Array.from(contentRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
        const first = focusables.find(el => !['Close', 'Back'].includes(el.getAttribute('aria-label') || ''));
        (first || contentRef.current).focus({ preventScroll: true });
    }, [open]);

    const enter = 'opacity var(--duration-modal, 280ms) var(--ease-out, ease-out), transform var(--duration-modal, 280ms) var(--ease-out, ease-out)';
    const exit = 'opacity 200ms var(--ease-out, ease-out), transform 200ms var(--ease-out, ease-out)';
    const transition = open ? enter : exit;

    const panelCls = isMobile
        ? 'absolute inset-0 flex flex-col bg-white'
        : `absolute inset-y-0 right-0 w-[min(88vw,100%)] ${width} flex flex-col bg-white border-l border-slate-200 shadow-[-24px_0_48px_-12px_rgba(0,0,0,0.25)]`;

    return createPortal((
        <div
            role="dialog"
            aria-modal="true"
            aria-label={label}
            className="fixed inset-0 z-[100] bbm-slideover"
            data-bbm-slideover=""
        >
            {!isMobile && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]"
                    style={{ opacity: open ? 1 : 0, transition }}
                    onClick={requestClose}
                />
            )}
            <div
                ref={contentRef}
                tabIndex={-1}
                className={`${panelCls} focus:outline-none`}
                style={{
                    opacity: open ? 1 : 0,
                    transform: open ? 'translateX(0)' : 'translateX(24px)',
                    transition,
                }}
            >
                <div className="flex items-center gap-2 px-3 md:px-4 py-2.5 border-b border-slate-200 flex-shrink-0 min-w-0"
                    style={isMobile ? { paddingTop: 'max(env(safe-area-inset-top), 10px)' } : undefined}>
                    {isMobile && (
                        <IconButton label="Back" onClick={requestClose} className="-ml-1 flex-shrink-0 bbm-tip-below">
                            <IconChevronLeft size={22} />
                        </IconButton>
                    )}
                    <div className="flex items-center gap-2 flex-1 min-w-0">{header}</div>
                    {!isMobile && (
                        <IconButton label="Close" onClick={requestClose} className="flex-shrink-0 bbm-tip-below bbm-tip-left">
                            <IconX size={18} />
                        </IconButton>
                    )}
                </div>
                {strip && <div className="flex-shrink-0 border-b border-slate-200">{strip}</div>}
                <div className={`flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 md:px-5 py-4 ${bodyClassName}`}>
                    {children}
                </div>
                {footer && (
                    <div className="flex-shrink-0 border-t border-slate-200 bg-white px-4 md:px-5 py-3"
                        style={isMobile ? { paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' } : undefined}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    ), portalRoot());
}
